import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  Chip,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Fab,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import BiotechIcon from "@mui/icons-material/Biotech";
import StarIcon from "@mui/icons-material/Star";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PlaceIcon from "@mui/icons-material/Place";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useSelector, useDispatch } from "react-redux";
import { getSettingsAction } from "../redux/actions/settings";
import logoImg from "../assets/images/logo_big_bright.png";

const BORDEAUX = "#401119";
const CREAM = "#f4edde";
const DARK = "#2a2a2a";

const NAV_LINKS = [
  { label: "Servicios", id: "servicios" },
  { label: "Especialistas", id: "especialistas" },
  { label: "Ecografías", id: "ecografias" },
  { label: "Convenios", id: "convenios" },
  { label: "Contacto", id: "contacto" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const SectionTitle = ({ children, light }) => (
  <Typography
    gutterBottom
    sx={{
      fontFamily: "Halu, sans-serif",
      fontSize: { xs: 34, md: 44 },
      fontWeight: 400,
      color: light ? CREAM : BORDEAUX,
      lineHeight: 1.15,
      mb: 4,
    }}
  >
    {children}
  </Typography>
);

export default function Home() {
const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const settings = useSelector((state) => state.settings);

  useEffect(() => {
    dispatch(getSettingsAction());
  }, [dispatch]);

  const SERVICES = settings.consultas;
  const STUDIES = settings.estudios;
  const ULTRASOUNDS = settings.ecografias;
  const DISCOUNTS = settings.convenios;
  const SPECIALTIES = settings.especialidades;
  const contacto = settings.contacto;
  const modeloEco = settings.modeloEco;

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <Box sx={{ fontFamily: "Roboto, Arial, sans-serif" }}>
      {/* ── NAVBAR ── */}
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          backgroundColor: scrolled ? BORDEAUX : "transparent",
          transition: "background-color 0.3s",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {/* Logo */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <img
              src={logoImg}
              alt="Servital"
              style={{ height: 36, mixBlendMode: "screen" }}
            />
            <Typography
              sx={{
                color: CREAM,
                fontFamily: "Halu, sans-serif",
                fontSize: 20,
                letterSpacing: 1,
                display: { xs: "none", sm: "block" },
              }}
            >
              Servital Salud
            </Typography>
          </Box>

          {/* Desktop nav */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, alignItems: "center" }}>
            {NAV_LINKS.map((l) => (
              <Button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                sx={{ color: CREAM, textTransform: "none", fontWeight: 400 }}
              >
                {l.label}
              </Button>
            ))}
          </Box>

          {/* Mobile menu */}
          <IconButton
            sx={{ display: { xs: "flex", md: "none" }, color: CREAM }}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 220, pt: 2 }}>
          <List>
            {NAV_LINKS.map((l) => (
              <ListItemButton
                key={l.id}
                onClick={() => { scrollTo(l.id); setDrawerOpen(false); }}
              >
                <ListItemText primary={l.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* ── HERO ── */}
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: BORDEAUX,
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 14, md: 0 } }}>
          <Grid container alignItems="center" spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontFamily: "Halu, sans-serif",
                  fontSize: { xs: 48, sm: 62, md: 76 },
                  color: CREAM,
                  lineHeight: 1.1,
                  mb: 3,
                }}
              >
                Un plan de salud a tu alcance
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(244,237,222,0.72)",
                  fontWeight: 300,
                  mb: 5,
                  lineHeight: 1.7,
                }}
              >
                {SPECIALTIES.length} especialidades médicas, equipos de imagen de última
                generación y atención de alta prioridad — en Barinas, para
                Barinas.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => scrollTo("servicios")}
                  sx={{
                    backgroundColor: CREAM,
                    color: BORDEAUX,
                    fontWeight: 700,
                    textTransform: "none",
                    px: 4,
                    borderRadius: 2,
                    "&:hover": { backgroundColor: "#e8e0d0" },
                  }}
                >
                  Ver servicios
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollTo("contacto")}
                  sx={{
                    borderColor: "rgba(244,237,222,0.5)",
                    color: CREAM,
                    textTransform: "none",
                    px: 4,
                    borderRadius: 2,
                    "&:hover": { backgroundColor: "rgba(244,237,222,0.08)" },
                  }}
                >
                  Contacto
                </Button>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 300,
                  height: 300,
                  borderRadius: "50%",
                  backgroundColor: "rgba(244,237,222,0.1)",
                  border: "1.5px solid rgba(244,237,222,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 0 80px rgba(244,237,222,0.07)",
                }}
              >
                <img
                  src={logoImg}
                  alt="Servital Salud"
                  style={{ width: "60%", mixBlendMode: "screen" }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── PILLARS ── */}
      <Box sx={{ backgroundColor: DARK, py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {[
              {
                icon: <LocalHospitalIcon sx={{ fontSize: 36, color: CREAM }} />,
                title: `${SPECIALTIES.length} Especialidades`,
                text: "Cobertura médica integral para toda la familia.",
              },
              {
                icon: <BiotechIcon sx={{ fontSize: 36, color: CREAM }} />,
                title: "Equipos de última generación",
                text: "Ecógrafo Mindray Z60 y estudios cardíacos avanzados.",
              },
              {
                icon: <StarIcon sx={{ fontSize: 36, color: CREAM }} />,
                title: "Alta prioridad",
                text: "Citas preferentes para atletas y trabajadores afiliados.",
              },
            ].map((p) => (
              <Grid key={p.title} item xs={12} sm={4} sx={{ textAlign: "center" }}>
                <Box sx={{ mb: 1.5 }}>{p.icon}</Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: CREAM, mb: 0.5 }}
                >
                  {p.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(244,237,222,0.6)" }}>
                  {p.text}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── SERVICES ── */}
      <Box id="servicios" sx={{ backgroundColor: CREAM, py: 10 }}>
        <Container maxWidth="lg">
          <SectionTitle>Consultas Médicas</SectionTitle>
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {SERVICES.map((s) => (
              <Grid key={s.group} item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                <Box
                  sx={{
                    border: `1.5px solid ${BORDEAUX}`,
                    borderRadius: 2,
                    p: 3,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                  }}
                >
                  <Typography
                    variant="h3"
                    fontWeight={800}
                    sx={{ color: BORDEAUX, mb: 0.5 }}
                  >
                    {s.price}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    {s.group}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", mb: 1.5, display: "block" }}
                  >
                    {s.note}
                  </Typography>
                  {s.items.length > 0 && (
                    <Box component="ul" sx={{ pl: 2, m: 0, mt: 1.5 }}>
                      {s.items.map((it) => (
                        <Typography
                          key={it}
                          component="li"
                          variant="body2"
                          sx={{ mb: 0.25 }}
                        >
                          {it}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ mb: 6 }} />

          <SectionTitle>Estudios e Imágenes</SectionTitle>
          <Grid container spacing={2}>
            {STUDIES.map((s) => (
              <Grid key={s.name} item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    borderBottom: "1px solid rgba(0,0,0,0.1)",
                    pb: 1.5,
                    pt: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {s.name}
                    </Typography>
                    {s.note && (
                      <Typography variant="caption" color="text.secondary">
                        {s.note}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: BORDEAUX, ml: 2, whiteSpace: "nowrap" }}
                  >
                    {s.price}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── SPECIALTIES ── */}
      <Box id="especialistas" sx={{ backgroundColor: BORDEAUX, py: 10 }}>
        <Container maxWidth="lg">
          <SectionTitle light>Nuestros Especialistas</SectionTitle>
          <Typography
            variant="body1"
            sx={{ color: "rgba(244,237,222,0.7)", mb: 5, maxWidth: 560 }}
          >
            Contamos con profesionales de alto nivel en {SPECIALTIES.length} especialidades médicas,
            comprometidos con los más altos estándares de calidad y seguridad.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {SPECIALTIES.map((sp) => (
              <Chip
                key={sp}
                label={sp}
                sx={{
                  backgroundColor: "rgba(244,237,222,0.12)",
                  color: CREAM,
                  border: "1px solid rgba(244,237,222,0.25)",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "rgba(244,237,222,0.2)" },
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── ULTRASOUND ── */}
      <Box id="ecografias" sx={{ backgroundColor: "#f8f4ec", py: 10 }}>
        <Container maxWidth="lg">
          <SectionTitle>Ecografías</SectionTitle>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Equipo utilizado:{" "}
            <Box component="span" fontWeight={700} color={BORDEAUX}>
              {modeloEco}
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 5 }}>
            Diagnósticos precisos gracias a tecnología de imagen de última generación.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {ULTRASOUNDS.map((u) => (
              <Chip
                key={u}
                label={u}
                sx={{
                  backgroundColor: "rgba(64,17,25,0.07)",
                  color: BORDEAUX,
                  border: "1px solid rgba(64,17,25,0.25)",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "rgba(64,17,25,0.13)" },
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── PARTNERSHIPS ── */}
      <Box id="convenios" sx={{ backgroundColor: DARK, py: 10 }}>
        <Container maxWidth="lg">
          <SectionTitle light>Convenios y Descuentos</SectionTitle>
          <Typography
            variant="body2"
            sx={{ color: "rgba(244,237,222,0.6)", mb: 5, maxWidth: 520 }}
          >
            Nuestros afiliados acceden a descuentos exclusivos en laboratorios,
            farmacias e imagen diagnóstica con nuestros aliados estratégicos.
          </Typography>
          <Grid container spacing={3}>
            {DISCOUNTS.map((d) => (
              <Grid key={d.category} item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    borderLeft: `3px solid ${CREAM}`,
                    pl: 2.5,
                    py: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <LocalOfferIcon sx={{ fontSize: 16, color: CREAM }} />
                    <Typography variant="h6" fontWeight={700} sx={{ color: CREAM }}>
                      {d.discount}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: CREAM, mb: 0.25 }}>
                    {d.category}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(244,237,222,0.55)" }}>
                    {d.partners}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CONTACT ── */}
      <Box id="contacto" sx={{ backgroundColor: CREAM, py: 10 }}>
        <Container maxWidth="lg">
          <SectionTitle>Contáctanos</SectionTitle>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
                <PlaceIcon sx={{ color: BORDEAUX, mt: 0.3 }} />
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    Barinas, Estado Barinas
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contacto.address}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <PhoneIcon sx={{ color: BORDEAUX }} />
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {contacto.phone}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {contacto.contactName}{contacto.contactTitle ? ` — ${contacto.contactTitle}` : ""}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: BORDEAUX,
                  borderRadius: 2,
                  p: 4,
                  color: CREAM,
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  ¿Eres empresa o club deportivo?
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                  Ofrecemos planes personalizados con tarifas competitivas y
                  atención de alta prioridad para tus colaboradores o atletas.
                </Typography>
                <Button
                  variant="outlined"
                  href="mailto:servital@servital-salud.com"
                  sx={{
                    color: CREAM,
                    borderColor: CREAM,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "rgba(244,237,222,0.1)" },
                  }}
                >
                  Solicitar propuesta
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── WHATSAPP FAB ── */}
      {contacto.whatsapp && (
        <Fab
          component="a"
          href={`https://wa.me/${contacto.whatsapp}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            position: "fixed",
            bottom: 28,
            right: 28,
            backgroundColor: "#25D366",
            color: "#fff",
            "&:hover": { backgroundColor: "#1ebe5d" },
            zIndex: 1300,
          }}
        >
          <WhatsAppIcon />
        </Fab>
      )}

      {/* ── FOOTER ── */}
      <Box sx={{ backgroundColor: "#1a1a1a", py: 4 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <img
                src={logoImg}
                alt="Servital"
                style={{ height: 28, mixBlendMode: "screen", opacity: 0.85 }}
              />
              <Typography variant="body2" sx={{ color: "rgba(244,237,222,0.5)" }}>
                SERVITAL SALUD C.A.
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "rgba(244,237,222,0.35)" }}>
              © {new Date().getFullYear()} Servital Salud. Todos los derechos reservados.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
